import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../legal-board/LegalBoardTab.css';
import './PublicUtilityTab.css';
import { can } from '../../../utils/permissions';
import { formatMoney } from '../../../utils/money';
import { formatDateCa } from '../legal-board/legalBoardUi';
import { Disclosure, StatusPill, StateCorrection } from '../legal-board/LegalBoardPrimitives';
import {
    fetchPublicUtilityOverview,
    updateRequirements,
    recordAssembly,
    applicationAction,
    regimeAction,
    councilAction,
    downloadIncomePdf,
    downloadAssemblyCertificate,
    downloadApplicationLetter,
    downloadCouncilLetter,
    preloadReport,
} from './api';
import { STEP_ORDER, STEP_TITLES, CHECK_LABELS, CONFIRMED_CHECKS, DISSOLUTION_CLAUSE } from './constants';
import PublicUtilityReport from './PublicUtilityReport';
import PublicUtilityDonors from './PublicUtilityDonors';
import PublicUtilityCalendar from './PublicUtilityCalendar';
import PublicUtilityObligation from './PublicUtilityObligation';
import { OfficialLink, DateForm, NoteForm } from './PublicUtilityForms';
import { headline, desDe } from './calendarCopy';

const STATE_LABELS = { done: 'Fet', active: 'Pas actual', pending: 'Pendent', waiting: 'Esperant resposta' };

const euros = (value) => `${formatMoney(value)} €`;

/** The header: one pill and one line saying where things stand. */
export function summarizePublicUtility(overview) {
    const { status, eligibility, obligations = [], badge } = overview;
    const yearsCheck = eligibility.checks.find((check) => check.key === 'years');
    switch (status.applicationStatus) {
    case 'granted': {
        if (!status.regimeOptedOn) return { pill: { tone: 'warning', label: 'Declarada' }, line: headline({ obligations, status }) };
        const pill = badge?.level === 'urgent' ? { tone: 'urgent', label: 'Cal actuar' } : badge?.level === 'soon' ? { tone: 'warning', label: 'Termini a prop' } : { tone: 'success', label: 'Declarada' };
        return { pill, line: headline({ obligations, status }) };
    }
    case 'submitted':
        return { pill: { tone: 'info', label: 'Presentada' }, line: `Presentada el ${formatDateCa(status.submittedOn)}. La Generalitat té fins a 6 mesos per respondre.` };
    case 'correction_required':
        return { pill: { tone: 'urgent', label: 'Cal corregir' }, line: 'La Generalitat demana corregir o afegir documentació.' };
    case 'rejected':
        return { pill: { tone: 'neutral', label: 'Denegada' }, line: `Denegada el ${formatDateCa(status.rejectedOn)}. Es pot tornar a demanar quan s’hagi resolt el que faltava.` };
    default:
        if (eligibility.ready) return { pill: { tone: 'info', label: 'Es pot demanar' }, line: 'La colla compleix els requisits: prepara els documents i presenta la sol·licitud.' };
        if (yearsCheck?.eligibleFrom && yearsCheck.ok === false) {
            return { pill: { tone: 'neutral', label: 'Sense demanar' }, line: `Es podrà demanar a partir del ${formatDateCa(yearsCheck.eligibleFrom)}, quan faci 2 anys que la colla és inscrita.` };
        }
        return { pill: { tone: 'neutral', label: 'Sense demanar' }, line: eligibility.pending === 1 ? 'Falta 1 requisit per poder-la demanar.' : `Falten ${eligibility.pending} requisits per poder-la demanar.` };
    }
}

function PublicUtilityTab({ userInfo, userId, onBadgeChange, onChangeTab }) {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [view, setView] = useState({ name: 'steps' }); // steps | report { year } | donors { year } | obligation { key, year }
    const [foldOpen, setFoldOpen] = useState(null);
    const [openSteps, setOpenSteps] = useState(null);
    const [activeForm, setActiveForm] = useState(null);
    const [registry, setRegistry] = useState({ registryNumber: '', registryInscribedOn: '' });
    const [assemblyDate, setAssemblyDate] = useState('');
    const [showHowTo, setShowHowTo] = useState(false);
    const [showStatutes, setShowStatutes] = useState(false);

    const hasAccess = can(userInfo, 'ACCESS_QUOTES');
    const canManage = overview ? Boolean(overview.canManage) : can(userInfo, 'MANAGE_PUBLIC_UTILITY');

    // The parent passes a new callback on every render: reading it from a ref
    // keeps a badge update from reloading the overview again and again
    const onBadgeChangeRef = useRef(onBadgeChange);
    onBadgeChangeRef.current = onBadgeChange;

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!userId || !hasAccess) {
            setLoading(false);
            return null;
        }
        if (!silent) setLoading(true);
        setError(null);
        try {
            const payload = await fetchPublicUtilityOverview(userId);
            setOverview(payload);
            setRegistry({ registryNumber: payload.status.registryNumber || '', registryInscribedOn: payload.status.registryInscribedOn || '' });
            setAssemblyDate(payload.status.assemblyAgreedOn || '');
            if (typeof onBadgeChangeRef.current === 'function') onBadgeChangeRef.current(payload.badge || null);
            return payload;
        } catch (loadError) {
            setError(loadError.message || 'No s’ha pogut carregar la utilitat pública.');
            return null;
        } finally {
            setLoading(false);
        }
    }, [userId, hasAccess]);

    useEffect(() => {
        load();
    }, [load]);

    // The memòries this tab links to (the two of the application and the one of
    // each pending accounts filing), fetched quietly so they open at once
    const preloadYears = overview && view.name !== 'report'
        ? [...new Set([
            ...(overview.reportYears || []).map((item) => item.year),
            ...(overview.obligations || []).filter((item) => item.key === 'accounts' && item.state !== 'done').map((item) => item.fiscalYear),
        ])].join(',')
        : '';
    useEffect(() => {
        if (!preloadYears || !userId) return undefined;
        const timer = setTimeout(() => {
            preloadYears.split(',').forEach((year) => preloadReport({ userId, year: Number(year) }).catch(() => {}));
        }, 300);
        return () => clearTimeout(timer);
    }, [preloadYears, userId]);

    const run = async (action, { reload = true } = {}) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            await action();
            if (reload) {
                setActiveForm(null);
                await load({ silent: true });
            }
        } catch (actionError) {
            setError(actionError.message || 'No s’ha pogut desar.');
        } finally {
            setBusy(false);
        }
    };

    if (!hasAccess) {
        return (
            <div className="legal-board-tab">
                <div className="legal-board-card legal-board-empty">
                    <p>No tens accés a Tresoreria.</p>
                </div>
            </div>
        );
    }
    if (loading && !overview) {
        return (
            <div className="legal-board-tab">
                <div className="legal-board-card legal-board-empty" role="status" aria-live="polite">
                    <div className="legal-board-spinner"></div>
                    <p>Carregant la utilitat pública…</p>
                </div>
            </div>
        );
    }
    if (!overview) {
        return (
            <div className="legal-board-tab">
                <div className="legal-board-card legal-board-empty">
                    <p>{error || 'No s’ha pogut carregar la utilitat pública.'}</p>
                    <button type="button" className="legal-board-button primary" onClick={() => load()}>Reintentar</button>
                </div>
            </div>
        );
    }

    if (view.name === 'report') {
        return (
            <div className="legal-board-tab">
                <PublicUtilityReport userId={userId} year={view.year} canManage={canManage} onBack={() => { setView(view.from || { name: 'steps' }); load({ silent: true }); }} />
            </div>
        );
    }
    if (view.name === 'obligation') {
        const item = overview.obligations.find((obligation) => obligation.key === view.key && obligation.fiscalYear === view.year);
        if (item) {
            return (
                <div className="legal-board-tab">
                    <PublicUtilityObligation
                        key={`${item.key}-${item.fiscalYear}-${item.state}`}
                        item={item}
                        overview={overview}
                        userId={userId}
                        canManage={canManage}
                        onBack={() => { setView({ name: 'steps' }); load({ silent: true }); }}
                        onOpenDonors={(year, startAt) => setView({ name: 'donors', year, startAt, from: view })}
                        onOpenReport={(year) => setView({ name: 'report', year, from: view })}
                    />
                </div>
            );
        }
    }
    if (view.name === 'donors') {
        return (
            <div className="legal-board-tab">
                <PublicUtilityDonors userId={userId} year={view.year} collaNif={overview.colla.nif} canManage={canManage} startAt={view.startAt} onBack={() => { setView(view.from || { name: 'steps' }); load({ silent: true }); }} />
            </div>
        );
    }

    const { status, eligibility, steps, obligations, links, fees } = overview;
    const summary = summarizePublicUtility(overview);
    const granted = status.applicationStatus === 'granted';
    // Once declared the first four steps fold into one row and the calendar replaces the fifth
    const stepOrder = granted ? STEP_ORDER.filter((key) => key !== 'yearly') : STEP_ORDER;
    const activeKey = stepOrder.find((key) => steps[key].state === 'active') || null;
    const isOpen = (key) => (openSteps ? Boolean(openSteps[key]) : key === activeKey);
    const folded = foldOpen === null ? !status.regimeOptedOn : foldOpen;
    const toggle = (key) => setOpenSteps((current) => {
        const base = current || Object.fromEntries(stepOrder.map((item) => [item, isOpen(item)]));
        return { ...base, [key]: !base[key] };
    });
    const checks = Object.fromEntries(eligibility.checks.map((check) => [check.key, check]));
    const [firstYear, secondYear] = overview.reportYears.map((item) => item.year);

    // -- Step 1 · Requirements ------------------------------------------------

    const checkIcon = (ok) => (ok === true ? 'fa-check-circle' : ok === false ? 'fa-times-circle' : 'fa-question-circle');

    const renderCheck = (key, extra = null) => (
        <li key={key} className={`public-utility-check ${checks[key]?.ok === true ? 'ok' : checks[key]?.ok === false ? 'missing' : 'unknown'}`}>
            <i className={`fas ${checkIcon(checks[key]?.ok)}`} aria-hidden="true"></i>
            <div className="public-utility-check-body">
                <span className="legal-board-sr-only">{checks[key]?.ok === true ? 'Complert. ' : checks[key]?.ok === false ? 'No complert. ' : 'Sense respondre. '}</span>
                <span>{CHECK_LABELS[key]}</span>
                {extra}
            </div>
        </li>
    );

    const renderConfirm = (key, extra = null) => (
        <li key={key} className="public-utility-check public-utility-confirm">
            <label>
                <input
                    type="checkbox"
                    checked={checks[key]?.ok === true}
                    disabled={!canManage || busy}
                    onChange={(event) => run(() => updateRequirements({ userId, requirements: { [CONFIRMED_CHECKS[key]]: event.target.checked } }))}
                />
                <span>{CHECK_LABELS[key]}</span>
            </label>
            {extra}
        </li>
    );

    const registryChanged = registry.registryNumber !== (status.registryNumber || '') || registry.registryInscribedOn !== (status.registryInscribedOn || '');

    const renderRequirements = () => (
        <>
            <p>
                Amb la declaració, el que donen socis i patrocinadors desgrava a la renda (el 80% dels primers 250 € de l’any).
                {' '}A canvi, cada any cal presentar els comptes i la llista de donants.
            </p>
            <ul className="public-utility-checks" aria-label="Requisits">
                {renderCheck('years', (
                    <div className="legal-board-form-grid public-utility-registry">
                        <div className="legal-board-field">
                            <label htmlFor="public-utility-inscribed">Data d’inscripció de la colla</label>
                            <input id="public-utility-inscribed" type="date" value={registry.registryInscribedOn} disabled={!canManage || busy} onChange={(event) => setRegistry((value) => ({ ...value, registryInscribedOn: event.target.value }))} />
                        </div>
                        <div className="legal-board-field">
                            <label htmlFor="public-utility-registry-number">Número d’inscripció</label>
                            <input id="public-utility-registry-number" type="text" value={registry.registryNumber} disabled={!canManage || busy} onChange={(event) => setRegistry((value) => ({ ...value, registryNumber: event.target.value }))} />
                        </div>
                        {canManage && registryChanged ? (
                            <button type="button" className="legal-board-button secondary" disabled={busy} onClick={() => run(() => updateRequirements({ userId, requirements: registry }))}>Desar</button>
                        ) : null}
                        {checks.years?.ok === false && checks.years.eligibleFrom ? (
                            <p className="legal-board-muted legal-board-small">Es podrà demanar a partir del {formatDateCa(checks.years.eligibleFrom)}.</p>
                        ) : null}
                    </div>
                ))}
                {renderCheck('board', checks.board?.ok ? null : (
                    <p className="legal-board-muted legal-board-small">
                        Es porta a <button type="button" className="legal-board-inline-action" onClick={() => onChangeTab?.('junta-legal')}>Registre</button>.
                    </p>
                ))}
                {renderCheck('nif', checks.nif?.ok ? null : (
                    <p className="legal-board-muted legal-board-small">
                        Afegeix-lo a <button type="button" className="legal-board-inline-action" onClick={() => onChangeTab?.('info')}>Dades fiscals</button>.
                    </p>
                ))}
                {renderConfirm('statutes', (
                    <Disclosure id="public-utility-statutes" summary="Què han de dir" open={showStatutes} onToggle={() => setShowStatutes((value) => !value)}>
                        <p>Per desgravar, la Llei 49/2002 demana que, si la colla es dissol, tot el que té vagi a una entitat del mateix règim o a una administració. Busca-ho a l’article de la dissolució. Hauria de dir alguna cosa així:</p>
                        <p className="public-utility-example">{DISSOLUTION_CLAUSE}</p>
                        <p className="legal-board-muted legal-board-small">
                            Si no ho diu, l’assemblea ha d’aprovar el canvi i després s’inscriu al Registre: 34,85 € i fins a 3 mesos (si no responen, queda aprovat). Ho pots fer mentre prepares la resta.
                            {links.statutes ? <>{' '}<OfficialLink href={links.statutes}>Modificació dels estatuts</OfficialLink></> : null}
                        </p>
                    </Disclosure>
                ))}
                {renderConfirm('openActivities')}
                {renderConfirm('unpaidBoard')}
                {renderConfirm('keepsAccounts')}
            </ul>
            {!granted && canManage ? (
                activeForm === 'already' ? (
                    <DateForm id="public-utility-already" label="Data de la resolució" submitLabel="Desar" busy={busy} onSubmit={({ date }) => run(() => applicationAction({ userId, action: 'grant', date }))} onCancel={() => setActiveForm(null)} />
                ) : (
                    <p className="legal-board-muted legal-board-small">
                        Si la colla ja és d’utilitat pública, <button type="button" className="legal-board-inline-action" onClick={() => setActiveForm('already')} disabled={busy}>indica-ho aquí</button>.
                        {' '}Ho pots comprovar a la <OfficialLink href={links.list}>llista de la Generalitat</OfficialLink>.
                    </p>
                )
            ) : null}
        </>
    );

    // -- Step 2 · Documents ---------------------------------------------------

    const reportRow = (year) => {
        const saved = overview.reportYears.find((item) => item.year === year)?.saved;
        return (
            <li key={year} className="public-utility-doc">
                <div>
                    <strong>Memòria d’activitats {year}</strong>
                    <span className="legal-board-muted legal-board-small">{saved ? ' · revisada' : ' · Aleta hi posa les actuacions, els assajos i els socis'}</span>
                </div>
                <button type="button" className="legal-board-button secondary small" onClick={() => setView({ name: 'report', year })}>
                    {canManage ? 'Revisar i descarregar' : 'Veure'}
                </button>
            </li>
        );
    };

    const renderDocuments = () => (
        <>
            <p>El tràmit demana aquests documents dels dos últims anys tancats, signats per la junta.</p>
            <ol className="public-utility-docs">
                {reportRow(firstYear)}
                {reportRow(secondYear)}
                <li className="public-utility-doc">
                    <div>
                        <strong>Comptes anuals {firstYear} i {secondYear}</strong>
                        <p className="legal-board-muted legal-board-small">El balanç i el compte de resultats. Els sol fer el gestor amb el model oficial (el simplificat, en Excel, és a la pàgina del tràmit). Aleta et dona els ingressos que hi consten.</p>
                    </div>
                    <div className="public-utility-doc-actions">
                        {[firstYear, secondYear].map((year) => (
                            <button key={year} type="button" className="legal-board-button link small" disabled={busy} onClick={() => run(() => downloadIncomePdf({ userId, year }), { reload: false })}>
                                <i className="fas fa-download" aria-hidden="true"></i> Ingressos {year}
                            </button>
                        ))}
                    </div>
                </li>
                <li className="public-utility-doc">
                    <div>
                        <strong>Certificat de l’acord de l’assemblea</strong>
                        <p className="legal-board-muted legal-board-small">L’assemblea general ha d’acordar demanar-la. Aleta hi posa la junta de Registre.</p>
                        {canManage ? (
                            <div className="public-utility-inline">
                                <label htmlFor="public-utility-assembly">Data de l’assemblea</label>
                                <input id="public-utility-assembly" type="date" value={assemblyDate} disabled={busy} onChange={(event) => setAssemblyDate(event.target.value)} />
                                {assemblyDate !== (status.assemblyAgreedOn || '') ? (
                                    <button type="button" className="legal-board-button secondary small" disabled={busy} onClick={() => run(() => recordAssembly({ userId, date: assemblyDate || null }))}>Desar</button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    {canManage && status.assemblyAgreedOn ? (
                        <button type="button" className="legal-board-button secondary small" disabled={busy} onClick={() => run(() => downloadAssemblyCertificate({ userId }), { reload: false })}>
                            <i className="fas fa-download" aria-hidden="true"></i> Descarregar
                        </button>
                    ) : null}
                </li>
                <li className="public-utility-doc">
                    <div>
                        <strong>Escrit de sol·licitud</strong>
                        <p className="legal-board-muted legal-board-small">Aleta el redacta amb les finalitats de la memòria i les activitats dels dos anys. Revisa’l i que el signi la presidència. Hi has d’afegir la còpia de la targeta del NIF.</p>
                    </div>
                    <button type="button" className="legal-board-button secondary small" disabled={busy} onClick={() => run(() => downloadApplicationLetter({ userId }), { reload: false })}>
                        <i className="fas fa-download" aria-hidden="true"></i> Descarregar
                    </button>
                </li>
                <li className="public-utility-doc">
                    <div>
                        <strong>Informe de l’ajuntament <span className="legal-board-muted">(recomanat)</span></strong>
                        <p className="legal-board-muted legal-board-small">La Generalitat recomana adjuntar un informe d’una administració que digui que la colla és d’interès general. Envia aquesta carta a l’ajuntament i, quan te’l facin, adjunta’l al tràmit.</p>
                    </div>
                    <button type="button" className="legal-board-button secondary small" disabled={busy} onClick={() => run(() => downloadCouncilLetter({ userId }), { reload: false })}>
                        <i className="fas fa-download" aria-hidden="true"></i> Carta per demanar-lo
                    </button>
                </li>
            </ol>
        </>
    );

    // -- Step 3 · Application -------------------------------------------------

    const APPLICATION_CORRECTIONS = {
        submitted: { label: 'Desmarcar com a presentada', description: 'Tornarà a quedar pendent de presentar a Aleta. No retira la sol·licitud.' },
        correction_required: { label: 'Treure el requeriment', description: 'Tornarà a constar com a presentada.' },
        granted: { label: 'Desmarcar com a concedida', description: 'També es desmarca el Model 036 i l’app deixarà de dir que els donatius desgraven.' },
        rejected: { label: 'Desmarcar com a denegada', description: 'Tornarà a constar com a presentada.' },
    };

    const renderApplicationActions = () => {
        if (!canManage || granted) return null;
        if (activeForm === 'submit') {
            return <DateForm id="public-utility-submit" label="Data de presentació" withReference submitLabel="Desar la presentació" busy={busy} onSubmit={({ date, reference }) => run(() => applicationAction({ userId, action: 'submit', date, reference }))} onCancel={() => setActiveForm(null)} />;
        }
        if (activeForm === 'grant') {
            return <DateForm id="public-utility-grant" label="Data de la resolució" submitLabel="Desar la resolució" busy={busy} onSubmit={({ date }) => run(() => applicationAction({ userId, action: 'grant', date }))} onCancel={() => setActiveForm(null)} />;
        }
        if (activeForm === 'reject') {
            return <DateForm id="public-utility-reject" label="Data de la resolució" submitLabel="Desar" busy={busy} onSubmit={({ date }) => run(() => applicationAction({ userId, action: 'reject', date }))} onCancel={() => setActiveForm(null)} />;
        }
        if (activeForm === 'correction') {
            return <NoteForm id="public-utility-correction" label="Què demana la Generalitat?" submitLabel="Desar el requeriment" busy={busy} onSubmit={({ note }) => run(() => applicationAction({ userId, action: 'correction', note }))} onCancel={() => setActiveForm(null)} />;
        }
        if (status.applicationStatus === 'submitted') {
            return (
                <div className="legal-board-actions">
                    <button type="button" className="legal-board-button primary" onClick={() => setActiveForm('grant')} disabled={busy}>Ens l’han concedida</button>
                    <button type="button" className="legal-board-button link" onClick={() => setActiveForm('correction')} disabled={busy}>Demanen corregir documentació</button>
                    <button type="button" className="legal-board-button link" onClick={() => setActiveForm('reject')} disabled={busy}>Ens l’han denegada</button>
                </div>
            );
        }
        if (status.applicationStatus === 'correction_required') {
            return (
                <div className="legal-board-actions">
                    <button type="button" className="legal-board-button primary" onClick={() => setActiveForm('submit')} disabled={busy}>Ja hem enviat la correcció</button>
                    <button type="button" className="legal-board-button link" onClick={() => setActiveForm('grant')} disabled={busy}>Ens l’han concedida</button>
                </div>
            );
        }
        return (
            <div className="legal-board-actions">
                <button type="button" className={`legal-board-button ${eligibility.ready ? 'primary' : 'secondary'}`} onClick={() => setActiveForm('submit')} disabled={busy}>
                    {status.applicationStatus === 'rejected' ? 'Marcar com a presentada de nou' : 'Marcar com a presentada'}
                </button>
            </div>
        );
    };

    const describeApplication = () => {
        switch (status.applicationStatus) {
        case 'granted': return `Concedida per resolució del ${formatDateCa(status.grantedOn)}.`;
        case 'submitted': return `Presentada el ${formatDateCa(status.submittedOn)}${status.gencatReference ? ` (${status.gencatReference})` : ''}. Si en 6 mesos no hi ha resposta, s’entén denegada.`;
        case 'correction_required': return `La Generalitat demana: ${status.correctionNote || 'corregir o afegir documentació'}. Hi ha 10 dies hàbils per respondre.`;
        case 'rejected': return `Denegada el ${formatDateCa(status.rejectedOn)}.`;
        default: return 'Quan tinguis els documents, presenta la sol·licitud a la Generalitat.';
        }
    };

    const renderApplication = () => {
        const correction = canManage ? APPLICATION_CORRECTIONS[status.applicationStatus] : null;
        const howToOpen = showHowTo || status.applicationStatus === 'not_started';
        return (
            <>
                <p>{describeApplication()}</p>
                {renderApplicationActions()}
                {correction && !activeForm ? <StateCorrection id="public-utility-application-revert" {...correction} onClick={() => run(() => applicationAction({ userId, action: 'revert' }))} disabled={busy} /> : null}
                <Disclosure id="public-utility-howto" summary="Com es fa el tràmit" open={howToOpen} onToggle={() => setShowHowTo(!howToOpen)}>
                    <ol className="legal-board-list legal-board-procedure">
                        <li>Entra al <OfficialLink href={links.application}>tràmit de declaració d’utilitat pública</OfficialLink> amb el certificat digital de la colla o de qui la representa.</li>
                        <li>Adjunta els documents del pas 2: les dues memòries, els comptes, el certificat de l’assemblea, l’escrit i el NIF, i l’informe de l’ajuntament si el teniu.</li>
                        <li>Quan arribi la carta de pagament, paga la taxa de {euros(fees.application)} en 10 dies hàbils; si no, l’arxiven.</li>
                        <li>Les respostes arriben per e-NOTUM al correu de la colla. Quan arribi la resolució, marca-la aquí.</li>
                    </ol>
                </Disclosure>
            </>
        );
    };

    // -- Step 4 · Llei 49/2002 ------------------------------------------------

    const renderRegime = () => {
        if (!granted) return <p className="legal-board-muted">Després de la declaració.</p>;
        return (
            <>
                {status.regimeOptedOn ? (
                    <p>
                        Acollida a la Llei 49/2002 des del {formatDateCa(status.regimeOptedOn)}. L’app ja diu als socis que el que aporten desgrava.
                    </p>
                ) : (
                    <p>
                        La declaració sola no fa que els donatius desgravin: cal dir a l’Agència Tributària, amb el <OfficialLink href={links.model036}>Model 036</OfficialLink>,
                        {' '}que la colla s’acull al règim de la Llei 49/2002. Val ja per a l’any en què es presenta.
                    </p>
                )}
                {canManage && !status.regimeOptedOn ? (
                    activeForm === 'regime' ? (
                        <DateForm id="public-utility-regime" label="Data del Model 036" submitLabel="Desar" busy={busy} onSubmit={({ date }) => run(() => regimeAction({ userId, action: 'opt', date }))} onCancel={() => setActiveForm(null)} />
                    ) : (
                        <div className="legal-board-actions">
                            <button type="button" className="legal-board-button primary" onClick={() => setActiveForm('regime')} disabled={busy}>Ja hem presentat el 036</button>
                        </div>
                    )
                ) : null}
                {canManage && status.regimeOptedOn ? (
                    <StateCorrection id="public-utility-regime-revert" label="Desmarcar el Model 036" description="L’app deixarà de dir als socis que el que aporten desgrava." onClick={() => run(() => regimeAction({ userId, action: 'revert' }))} disabled={busy} />
                ) : null}
                {status.regimeOptedOn ? (
                    <div className="public-utility-council">
                        <p className="legal-board-small">
                            {status.councilNotifiedOn
                                ? `Comunicat a l’ajuntament el ${formatDateCa(status.councilNotifiedOn)} (exempció de l’IBI).`
                                : 'Si la colla té un local en propietat, comunica-ho també a l’ajuntament per quedar exempta de l’IBI.'}
                        </p>
                        {canManage ? (
                            activeForm === 'council' ? (
                                <DateForm id="public-utility-council" label="Data de la comunicació" submitLabel="Desar" busy={busy} onSubmit={({ date }) => run(() => councilAction({ userId, action: 'notify', date }))} onCancel={() => setActiveForm(null)} />
                            ) : status.councilNotifiedOn ? (
                                <button type="button" className="legal-board-button link small" onClick={() => run(() => councilAction({ userId, action: 'revert' }))} disabled={busy}>Desmarcar</button>
                            ) : (
                                <button type="button" className="legal-board-button link small" onClick={() => setActiveForm('council')} disabled={busy}>Marcar com a comunicat</button>
                            )
                        ) : null}
                    </div>
                ) : null}
            </>
        );
    };

    // -- Step 5 · Every year --------------------------------------------------

    const renderYearly = () => <p className="legal-board-muted">Un cop declarada, aquí hi haurà el calendari de cada any i avisos abans de cada termini.</p>;

    const bodies = { requirements: renderRequirements, documents: renderDocuments, application: renderApplication, regime: renderRegime, yearly: renderYearly };

    const renderSteps = () => (
        <ol className="legal-board-flow" aria-label="Passos">
            {stepOrder.map((key, index) => {
                const step = steps[key];
                const open = isOpen(key);
                const iconState = step.state === 'waiting' ? 'active' : step.state;
                return (
                    <li key={key} className={`legal-board-flow-item ${iconState}`}>
                        <button type="button" className="legal-board-flow-head" aria-expanded={open} aria-controls={`public-utility-flow-${key}`} onClick={() => toggle(key)}>
                            <span className={`legal-board-flow-icon ${iconState}`} aria-hidden="true">
                                {step.state === 'done' ? <i className="fas fa-check"></i> : index + 1}
                            </span>
                            <span className="legal-board-sr-only">{STATE_LABELS[step.state]}. </span>
                            <span className="legal-board-flow-title">{STEP_TITLES[key]}</span>
                            <span className="legal-board-flow-summary">{stepSummary(key)}</span>
                            <i className={`fas fa-chevron-${open ? 'up' : 'down'} legal-board-flow-chevron`} aria-hidden="true"></i>
                        </button>
                        {open ? <div id={`public-utility-flow-${key}`} className="legal-board-flow-body">{bodies[key]()}</div> : null}
                    </li>
                );
            })}
        </ol>
    );

    // Declared: steps 1–4 in one row (open while the Model 036 is missing), then the calendar
    const renderDeclared = () => (
        <>
            <ol className="legal-board-flow" aria-label="Declaració">
                <li className={`legal-board-flow-item ${status.regimeOptedOn ? 'done' : 'active'}`}>
                    <button type="button" className="legal-board-flow-head" aria-expanded={folded} aria-controls="public-utility-declared" onClick={() => setFoldOpen(!folded)}>
                        <span className={`legal-board-flow-icon ${status.regimeOptedOn ? 'done' : 'active'}`} aria-hidden="true"><i className="fas fa-check"></i></span>
                        <span className="legal-board-flow-title">Declarada d’utilitat pública</span>
                        <span className="legal-board-flow-summary">
                            {formatDateCa(status.grantedOn)} · {status.regimeOptedOn ? `desgrava ${desDe(status.regimeOptedOn)}` : 'falta el Model 036'}
                        </span>
                        <i className={`fas fa-chevron-${folded ? 'up' : 'down'} legal-board-flow-chevron`} aria-hidden="true"></i>
                    </button>
                    {folded ? <div id="public-utility-declared" className="legal-board-flow-body public-utility-declared-body">{renderSteps()}</div> : null}
                </li>
            </ol>
            {obligations.length > 0 ? (
                <PublicUtilityCalendar
                    obligations={obligations}
                    donorTotals={overview.donorTotals || {}}
                    today={overview.today}
                    onOpen={(item) => setView({ name: 'obligation', key: item.key, year: item.fiscalYear })}
                />
            ) : (
                <p className="legal-board-muted">Els primers comptes es presenten l’any després de la declaració.</p>
            )}
        </>
    );

    const stepSummary = (key) => {
        const step = steps[key];
        if (key === 'requirements') return step.state === 'done' ? 'Complerts' : `${eligibility.pending === 1 ? 'Falta' : 'Falten'} ${eligibility.pending}`;
        if (key === 'documents') return step.state === 'done' ? 'Fet' : `${firstYear} i ${secondYear}`;
        if (key === 'application') return describeApplicationShort(status);
        if (key === 'regime') return status.regimeOptedOn ? capitalize(desDe(status.regimeOptedOn)) : granted ? 'Falta el 036' : 'Després de la declaració';
        return 'Després de la declaració';
    };

    return (
        <div className="legal-board-tab public-utility-tab">
            {!canManage ? (
                <p className="legal-board-readonly" role="note">
                    <i className="fas fa-eye" aria-hidden="true"></i> Només lectura: només un cap de junta pot fer canvis.
                </p>
            ) : null}
            <section className="legal-board-card legal-board-flow-card" aria-labelledby="public-utility-title">
                <header className="legal-board-card-head">
                    <h2 id="public-utility-title">Utilitat pública</h2>
                    <StatusPill tone={summary.pill.tone}>{summary.pill.label}</StatusPill>
                </header>
                <div className="legal-board-summary">
                    <p className={summary.pill.tone === 'urgent' && !granted ? 'legal-board-summary-alert' : 'legal-board-muted'}>{summary.line}</p>
                </div>
                {error ? <div className="legal-board-alert error" role="alert">{error}</div> : null}
                {granted ? renderDeclared() : renderSteps()}
            </section>
        </div>
    );
}

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

function describeApplicationShort(status) {
    switch (status.applicationStatus) {
    case 'granted': return `Concedida ${formatDateCa(status.grantedOn)}`;
    case 'submitted': return `Presentada ${formatDateCa(status.submittedOn)}`;
    case 'correction_required': return 'Cal corregir';
    case 'rejected': return 'Denegada';
    default: return 'Sense presentar';
    }
}

export default PublicUtilityTab;
